import { graphql } from "../../../__generated__/gql";
import { useMutation } from "urql";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "../../../ui/Modal/Modal";
import styles from "./SetupWixModal.module.scss";
import Label from "../../../ui/Label/Label";
import TextInput from "../../../ui/TextInput/TextInput";
import ConfirmationModal from "../../../ui/Modal/ConfirmationModal";

const SetupWixConnectionMutation = graphql(/* GraphQL */ `
  mutation setupWixConnection($input: SetupWixConnectionInput!) {
    setupWixConnection(input: $input) {
      id
      siteId
      connected
      settings {
        publish
      }
    }
  }
`);

const schema = z.object({
  siteId: z.string().min(1, "Site ID is required"),
  apiKey: z.string().min(1, "API key is required"),
});

type SetupWixFormData = z.infer<typeof schema>;

interface SetupWixModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
}

function SetupWixModal({ isOpen, onClose, onSubmit }: SetupWixModalProps) {
  const methods = useForm<SetupWixFormData>({
    resolver: zodResolver(schema),
  });

  const [, setupWixConnection] = useMutation(SetupWixConnectionMutation);

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  const onFormSubmit = async (data: SetupWixFormData) => {
    try {
      await setupWixConnection({
        input: {
          siteId: data.siteId,
          apiKey: data.apiKey,
        },
      });
      onSubmit();
    } catch {
      //
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Setup Wix connection"
      subtitle={
        <>
          For help configuring your Wix connection, you can check out our{" "}
          <a
            href="https://docs.macawhq.com/en/articles/11015873-setting-up-wix-connection"
            target="_blank"
            rel="noreferrer"
          >
            Wix setup guide
          </a>
        </>
      }
    >
      <ConfirmationModal
        onSubmit={handleSubmit(onFormSubmit)}
        onClose={onClose}
        confirmDisabled={!isValid || isSubmitting}
        loading={isSubmitting}
      >
        <div className={styles.container}>
          <Controller
            name="apiKey"
            control={methods.control}
            render={({ field }) => (
              <Label label="Enter your Wix API key">
                <TextInput placeholder="********" {...field} />
              </Label>
            )}
          />
          <Controller
            name="siteId"
            control={methods.control}
            render={({ field }) => (
              <Label label="Enter your Wix site ID">
                <TextInput
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  {...field}
                />
              </Label>
            )}
          />
        </div>
      </ConfirmationModal>
    </Modal>
  );
}

export default SetupWixModal;
